// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/chat/useChatViewport.ts
// 🔧 PURPOSE: Due cose del telefono che servono alla chat ospite:
//             - la rete (online/offline), per la coda dei messaggi e
//               l'avviso "Sei offline";
//             - la tastiera: su iPhone (e su Chrome Android dalla 108) la
//               tastiera riduce solo il visualViewport, la pagina resta alta
//               com'era e la testata della chat scivolava fuori dallo
//               schermo. Qui si misura la parte visibile, e la chat si
//               posiziona e si dimensiona su quella.
// ============================================

import { useCallback, useEffect, useState } from 'react';

/** navigator.onLine, ma solo quando dice "no" con certezza (undefined = online). */
export function isNavigatorOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

/**
 * true/false con gli eventi del browser. `markOnline` serve quando una
 * richiesta riesce mentre il browser dice offline (alcuni telefoni lo
 * sbagliano): la rete c'è.
 */
export function useOnlineStatus(): { online: boolean; markOnline: () => void } {
  const [online, setOnline] = useState<boolean>(isNavigatorOnline);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    // Cambiato tra il primo render e l'ascolto.
    setOnline(isNavigatorOnline());
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);
  const markOnline = useCallback(() => setOnline(true), []);
  return { online, markOnline };
}

/** La parte visibile dello schermo quando la tastiera la riduce. */
export interface ViewportBox {
  /** Altezza visibile (px). */
  height: number;
  /** Di quanto la parte visibile è scesa nella pagina (px). */
  top: number;
  /** La tastiera è aperta (la parte visibile è molto più bassa della pagina). */
  keyboard: boolean;
}

// Sotto questa differenza non è la tastiera (barre del browser che si
// allargano o si stringono).
const KEYBOARD_MIN_PX = 120;

/**
 * null quando la parte visibile è tutta la pagina (niente da correggere);
 * altrimenti altezza e posizione da dare alla chat. Senza visualViewport
 * (browser vecchi, test) sempre null. Con lo zoom a pizzico non si tocca
 * niente: l'ospite sta ingrandendo, non scrivendo.
 */
export function useKeyboardViewport(enabled: boolean): ViewportBox | null {
  const [box, setBox] = useState<ViewportBox | null>(null);

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!enabled || !vv) {
      setBox(null);
      return undefined;
    }
    let frame = 0;
    const measure = () => {
      frame = 0;
      const pageHeight = Math.max(
        typeof document !== 'undefined' && document.documentElement ? document.documentElement.clientHeight : 0,
        window.innerHeight || 0,
      );
      const height = Math.round(vv.height);
      const top = Math.max(0, Math.round(vv.offsetTop));
      const zoomed = typeof vv.scale === 'number' && vv.scale > 1.01;
      const keyboard = !zoomed && pageHeight - height > KEYBOARD_MIN_PX;
      if (zoomed || (!keyboard && top === 0) || height <= 0) {
        setBox((prev) => (prev ? null : prev));
        return;
      }
      setBox((prev) => (prev && prev.height === height && prev.top === top && prev.keyboard === keyboard
        ? prev
        : { height, top, keyboard }));
    };
    const schedule = () => {
      if (frame) return;
      frame = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(measure) : 0;
      if (!frame) measure();
    };
    measure();
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    return () => {
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
      if (frame && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
    };
  }, [enabled]);

  return enabled ? box : null;
}
